import { gql, useApolloClient } from "@apollo/client";
import { useCallback } from "react";
import { DataChannelReflectionTopicUpdateMessage } from "../../video-sdk";

const updatedAtFragment = gql`
  fragment PatientReflectionTopicUpdatedAt on PatientReflectionTopicObject {
    updatedAt
  }
`;

/**
 * Hook for updating the completion status of a reflection topic in the Apollo
 * cache without persisting changes to the backend. This is useful for
 * optimistic updates or temporary UI state changes.
 */
export function useUpdateCachedReflectionTopicCompletionStatusCallback() {
  const { cache } = useApolloClient();

  const updateTopicCompletion = useCallback(
    ({
      topicShortId,
      completed,
      updatedAt,
    }: DataChannelReflectionTopicUpdateMessage["topic"]) => {
      const topicCacheId = cache.identify({
        __typename: "PatientReflectionTopicObject",
        shortId: topicShortId,
      });

      if (!topicCacheId) {
        return;
      }

      const fragment = cache.readFragment<{ updatedAt: string }>({
        id: topicCacheId,
        fragment: updatedAtFragment,
      });

      if (!fragment) {
        return;
      }

      const { updatedAt: lastUpdatedAt } = fragment;
      const isOlderUpdate =
        updatedAt &&
        lastUpdatedAt &&
        new Date(updatedAt) <= new Date(lastUpdatedAt);

      // Ignore
      if (isOlderUpdate) {
        return;
      }

      const now = new Date().toISOString();
      cache.modify({
        id: topicCacheId,
        fields: {
          completedAt: () => (completed ? now : null),
          updatedAt: () => updatedAt ?? now,
        },
      });
    },
    [cache],
  );

  return updateTopicCompletion;
}
